import { LoginPage } from '@/pages/auth/LoginPage';

import { AccessDeniedPage } from '@/pages/auth/AccessDeniedPage';
import { HomePage } from '@/pages/auth/HomePage';
import { IndicadorDetallePage } from '@/pages/indicadores/IndicadorDetallePage';
import { NotFoundPage } from '@/pages/notfound/NotFoundPage';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';
import { ProtectedRoute } from '../auth/ProtectedRoute';
import { Layout } from '../components/layout';
import {
  AsesoriaDetallePage,
  CrearAsesoriaPage,
  EmpresaDetallePage,
  EvaluacionPage,
  FinalizarAsesoriaPage,
  NTCDetallePage,
  ReunionesPage,
} from '../pages';
import { DashboardAsesoriaMatrizPage } from '../pages/dashboard';
import { AsesoriaHistorialDetallePage } from '../pages/admin/AsesoriaHistorialDetallePage';
import { AdminLayout } from '../pages/admin/AdminLayout';
import { AdminDashboardPage } from '../pages/admin/AdminDashboardPage';
import { AdminAsesoresPage } from '../pages/admin/AdminAsesoresPage';
import { AdminAsesorDetallePage } from '../pages/admin/AdminAsesorDetallePage';
import {
  AdminEmpresasPage,
  EmpresaNtcsTab,
  EmpresaAsesoriasTab,
  EmpresaIndicadoresTab,
  EmpresaProgresoTab,
  EmpresaSnapshotsTab,
} from '../pages/admin/AdminEmpresasPage';
import { AdminEnCursoPage } from '../pages/admin/AdminEnCursoPage';
import { AdminRevisionPage } from '../pages/admin/AdminRevisionPage';
import { AdminRevisionDetailPage } from '../pages/admin/AdminRevisionDetailPage';
import { AdminMatricesPage } from '../pages/admin/AdminMatricesPage';
import { AdminReportesPage } from '../pages/admin/AdminReportesPage';
import { AdminNormasPage } from '../pages/admin/AdminNormasPage';
import { AdminIndicadoresPage } from '../pages/admin/AdminIndicadoresPage';

// Sysadmin modules (lazy-friendly structure)
import { SysadminLayout } from '@/pages/sysadmin/SysadminLayout';
import { GestionUsuariosPage } from '@/pages/sysadmin/GestionUsuariosPage';
import { GestionEmpresasPage } from '@/pages/sysadmin/GestionEmpresasPage';
import { GestionRequisitosPage } from '@/pages/sysadmin/GestionRequisitosPage';
import { GestionAsociacionesPage } from '@/pages/sysadmin/GestionAsociacionesPage';
import { GestionAuditoriaPage } from '@/pages/sysadmin/GestionAuditoriaPage';
import { GestionEvidenciasPage } from '@/pages/sysadmin/GestionEvidenciasPage';
import { GestionAsesoriasPage } from '@/pages/sysadmin/GestionAsesoriasPage';
import { GestionActasPage } from '@/pages/sysadmin/GestionActasPage';
import { CorreccionesAsesoriasPage } from '@/pages/sysadmin/CorreccionesAsesoriasPage';
import { PruebaActaAudiosPage } from '@/pages/sysadmin/PruebaActaAudiosPage';
import { RegenerarActasPage } from '@/pages/sysadmin/RegenerarActasPage';
import { LogsMongoPage } from '@/pages/sysadmin/LogsMongoPage';
import { PendientesActasPage } from '@/pages/sysadmin/PendientesActasPage';
import { AudiosAjenosPage } from '@/pages/sysadmin/AudiosAjenosPage';
import { AudiosDuplicadosRevisionPage } from '@/pages/sysadmin/AudiosDuplicadosRevisionPage';
import { GestionIssuesRoute } from '@/auth/GestionIssuesRoute';
import { GestionAnomaliasPage } from '@/pages/sysadmin/GestionAnomaliasPage';
import { AuditoriaDrivePage } from '@/pages/sysadmin/AuditoriaDrivePage';
import { GestionAudiosPage } from '@/pages/sysadmin/GestionAudiosPage';
import { GestionIndicadoresPage } from '@/pages/sysadmin/GestionIndicadoresPage';
import { MonitoreoLogsPage } from '@/pages/sysadmin/MonitoreoLogsPage';
import { CopiarDrivePanel } from '@/pages/sysadmin/CopiarDrivePanel';
import { DatabaseManagerPage } from '@/pages/sysadmin/DatabaseManagerPage';
import { DiagnosticoNtcPage } from '@/pages/sysadmin/DiagnosticoNtcPage';

const router = createBrowserRouter([
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    path: '/acceso-denegado',
    element: <AccessDeniedPage />,
  },

  {
    // Rutas protegidas para ADMIN y ASESOR
    element: <ProtectedRoute allowedRoles={['ADMIN', 'ASESOR']} />,
    children: [
      {
        path: '/',
        element: <Layout />,
        children: [
          { index: true, element: <HomePage /> },

          // ASESOR routes
          {
            path: 'empresa/:nit/:tab?',
            element: (
              <ProtectedRoute allowedRoles={['ASESOR']}>
                <EmpresaDetallePage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'empresa/:nit/nueva-asesoria',
            element: (
              <ProtectedRoute allowedRoles={['ASESOR']}>
                <CrearAsesoriaPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'empresa/:nit/asesoria/:asesoriaId',
            element: (
              <ProtectedRoute allowedRoles={['ASESOR']}>
                <AsesoriaDetallePage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'empresa/:nit/asesoria/:asesoriaId/reuniones',
            element: (
              <ProtectedRoute allowedRoles={['ASESOR']}>
                <ReunionesPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'empresa/:nit/asesoria/:asesoriaId/evaluacion',
            element: (
              <ProtectedRoute allowedRoles={['ASESOR']}>
                <EvaluacionPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'empresa/:nit/asesoria/:asesoriaId/finalizar',
            element: (
              <ProtectedRoute allowedRoles={['ASESOR']}>
                <FinalizarAsesoriaPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'empresa/:nit/ntc/:ntcId',
            element: (
              <ProtectedRoute allowedRoles={['ASESOR']}>
                <NTCDetallePage />
              </ProtectedRoute>
            ),
          },
          {
            path: '/empresa/:nit/indicador/:indicadorId',
            element: (
              <ProtectedRoute allowedRoles={['ASESOR', 'ADMIN']}>
                <IndicadorDetallePage />
              </ProtectedRoute>
            ),
          },

          // ADMIN routes
          {
            element: <ProtectedRoute allowedRoles={['ADMIN']} />,
            children: [
              {
                element: <AdminLayout />,
                children: [
                  { path: 'dashboard', element: <AdminDashboardPage /> },
                  { path: 'asesores', element: <AdminAsesoresPage /> },
                  { path: 'asesores/:id', element: <AdminAsesorDetallePage /> },
                  { path: 'empresas', element: <AdminEmpresasPage /> },
                  {
                    path: 'empresas/:nit',
                    element: <AdminEmpresasPage />,
                    children: [
                      { index: true, element: <Navigate to="ntcs" replace /> },
                      { path: 'ntcs', element: <EmpresaNtcsTab /> },
                      { path: 'asesorias', element: <EmpresaAsesoriasTab /> },
                      { path: 'indicadores', element: <EmpresaIndicadoresTab /> },
                      { path: 'progreso', element: <EmpresaProgresoTab /> },
                      { path: 'snapshots', element: <EmpresaSnapshotsTab /> },
                    ],
                  },
                  { path: 'en-curso', element: <AdminEnCursoPage /> },
                  { path: 'revision', element: <AdminRevisionPage /> },
                  { path: 'revision/:id', element: <AdminRevisionDetailPage /> },
                  { path: 'revision/:id/:tab', element: <AdminRevisionDetailPage /> },
                  { path: 'indicadores', element: <AdminIndicadoresPage /> },
                  { path: 'indicadores/:tab', element: <AdminIndicadoresPage /> },
                  { path: 'indicadores/:tab/:nit', element: <AdminIndicadoresPage /> },
                  { path: 'matrices', element: <AdminMatricesPage /> },
                  { path: 'reportes', element: <AdminReportesPage /> },
                  { path: 'normas', element: <AdminNormasPage /> },
                  { path: 'normas/:ntcId', element: <AdminNormasPage /> },
                  // Revisión de hallazgos: solo correos guardados con "+" (ver GestionIssuesRoute).
                  {
                    element: <GestionIssuesRoute />,
                    children: [
                      { path: 'hallazgos/actas', element: <PendientesActasPage /> },
                      { path: 'hallazgos/audios-de-otro-asesor', element: <AudiosAjenosPage /> },
                      { path: 'hallazgos/audios-duplicados', element: <AudiosDuplicadosRevisionPage /> },
                      // Direcciones anteriores, para no romper enlaces ya compartidos.
                      { path: 'issues/pendientes-actas', element: <Navigate to="/hallazgos/actas" replace /> },
                      { path: 'issues/audios-ajenos', element: <Navigate to="/hallazgos/audios-de-otro-asesor" replace /> },
                      { path: 'issues/audios-duplicados', element: <Navigate to="/hallazgos/audios-duplicados" replace /> },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },

  // ─── Sysadmin Admin Panel ─────────────────────────────────────
  {
    element: <ProtectedRoute allowedRoles={['SYSADMIN']} />,
    children: [
      {
        path: '/admin',
        element: <SysadminLayout />,
        children: [
          { index: true, element: <Navigate to="usuarios" replace /> },
          { path: 'usuarios', element: <GestionUsuariosPage /> },
          { path: 'empresas', element: <GestionEmpresasPage /> },
          { path: 'requisitos', element: <GestionRequisitosPage /> },
          { path: 'asociaciones', element: <GestionAsociacionesPage /> },
          { path: 'indicadores', element: <GestionIndicadoresPage /> },
          { path: 'auditoria', element: <GestionAuditoriaPage /> },
          { path: 'evidencias', element: <GestionEvidenciasPage /> },
          { path: 'asesorias', element: <GestionAsesoriasPage /> },
          { path: 'actas', element: <GestionActasPage /> },
          { path: 'regenerar-actas', element: <RegenerarActasPage /> },
          { path: 'prueba-acta-audios', element: <PruebaActaAudiosPage /> },
          { path: 'correcciones-asesorias', element: <CorreccionesAsesoriasPage /> },
          { path: 'pendientes-actas', element: <PendientesActasPage /> },
          { path: 'audios-ajenos', element: <AudiosAjenosPage /> },
          { path: 'audios-duplicados', element: <AudiosDuplicadosRevisionPage /> },
          { path: 'anomalias', element: <GestionAnomaliasPage /> },
          { path: 'auditoria-drive', element: <AuditoriaDrivePage /> },
          { path: 'grabaciones', element: <GestionAudiosPage /> },
          { path: 'logs', element: <MonitoreoLogsPage /> },
          { path: 'limpieza-logs', element: <LogsMongoPage /> },
          { path: 'drive', element: <CopiarDrivePanel /> },
          { path: 'database', element: <DatabaseManagerPage /> },
          { path: 'diagnostico-ntc', element: <DiagnosticoNtcPage /> },
        ],
      },
    ],
  },

  // ─── Admin Dashboard (for ADMIN + SYSADMIN) ───────────────────
  {
    element: <ProtectedRoute allowedRoles={['ADMIN', 'SYSADMIN']} />,
    children: [
      {
        path: 'admin/empresa/:nit/asesoria/:asesoriaId/matriz',
        element: <DashboardAsesoriaMatrizPage />,
      },
      {
        path: 'empresas/:nit/asesorias/:asesoriaId',
        element: <AsesoriaHistorialDetallePage />,
      },
    ],
  },

  { path: '*', element: <NotFoundPage /> },
]);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
